import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { getSharedTemplateDetailViewPublic } from "../utils/Services";
import NoteSectionPending from "../components/NoteSectionPending";
import { ITemplate } from "@/utils/Types";
import { NOTE } from "@/utils/constants";
import TemplateSection from "../components/TemplateSection";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { useAuthStore } from "@/utils/UseAuthStore";
import { ArrowRightIcon, LockClosedIcon } from "@heroicons/react/24/outline";

const SharedTemplatePublic = () => {
  const [template, setTemplate] = useState({} as ITemplate);
  const [isLoading, setIsLoading] = useState(true);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn());

  const { templateId = "" } = useParams<{
    templateId: string;
  }>();

  const navigate = useNavigate();

  useEffect(() => {
    // Apply dark mode settings on component mount
    const isDarkMode = localStorage.getItem("darkMode") === "true";
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, []);

  const fetchTemplate = async (identifier: string, loading = true) => {
    if (loading) setIsLoading(true);

    try {
      let response: any = {};

      if (!identifier) {
        navigate("/permission-denied");
        return;
      }
      response = await getSharedTemplateDetailViewPublic(identifier);

      setTemplate(response);
    } catch (error: any) {
      console.error(error);

      if (
        error.response &&
        (error.response.status === 403 || error.response.status === 404)
      ) {
        navigate("/permission-denied");
      }
    }
    if (loading) setIsLoading(false);
  };

  useEffect(() => {
    setIsLoading(true);

    const res = setTimeout(async () => {
      fetchTemplate(templateId);
    }, 500);

    return () => {
      clearTimeout(res);
    };
  }, [templateId]);

  const handleSignUp = () => {
    // Use navigate instead of window.location.href to prevent full page reloads
    // navigate("/signup");
    window.location.href = "https://www.waitlistr.com/lists/683824b6/kortex-ai";
  };

  const handleLogin = () => {
    // Use navigate instead of window.location.href to prevent full page reloads
    navigate("/signin");
  };

  return (
    <div className="container mx-auto py-6 px-4 md:px-6">
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:w-2/3">
          <div className="bg-background rounded-lg border border-border p-4 shadow-sm">
            <div className="h-[40px] mb-4 flex items-center">
              <div className="text-2xl font-bold">{template?.title}</div>
              <div className="text-2xl text-gray-500 ml-2">(Preview)</div>
            </div>

            <div className="max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
              {isLoading ? (
                <>
                  {[1, 2, 3, 4].map((index) => (
                    <div key={index} className="px-2 py-2 sm:gap-4 sm:px-6">
                      <NoteSectionPending />
                    </div>
                  ))}
                </>
              ) : (
                <div className="space-y-4">
                  {template?.content && (
                    <TemplateSection
                      key="content"
                      title={NOTE["content"]}
                      sectionKey="content"
                      insertVariable={() => {}}
                      showPopup={false}
                      variables={[]}
                    >
                      <div className="text-gray-600 dark:text-gray-200 text-base border-gray-300 dark:border-gray-600">
                        {template?.content}
                      </div>
                    </TemplateSection>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="w-full lg:w-1/3">
          <Card className="h-full flex flex-col justify-center">
            <CardHeader className="text-center">
              <CardTitle className="flex items-center justify-center gap-2">
                <LockClosedIcon className="h-5 w-5" />
                Want to use this template?
              </CardTitle>
              <CardDescription>
                Sign up or log in to use this template for your own notes.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="rounded-lg bg-muted p-4 text-center">
                  <h3 className="font-medium mb-2">Benefits of signing up:</h3>
                  <ul className="list-disc text-left space-y-1 text-sm inline-block mx-auto">
                    <li>Customize templates to fit your needs</li>
                    <li>Save templates for future use</li>
                    <li>Create and share your own templates</li>
                    <li>Access your templates from anywhere</li>
                  </ul>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-2">
              <Button className="w-full" onClick={handleSignUp}>
                Sign Up <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleLogin}
              >
                Already have an account? Log In
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SharedTemplatePublic;
